import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Show, SignInButton, UserButton } from "@clerk/react";
import { Menu, X, Wallet as WalletIcon, Heart } from "lucide-react";
import { useAccount } from "@/lib/account";
import { usePrefs } from "@/lib/prefs";
import PrefsSwitcher from "@/components/PrefsSwitcher";

const LINKS = [
  { to: "/browse", label: "Browse", id: "browse" },
  { to: "/countries", label: "Countries", id: "countries" },
  { to: "/plans", label: "Plans", id: "plans" },
  { to: "/interests", label: "Interests", id: "interests" },
  { to: "/chat", label: "Messages", id: "chat" },
  { to: "/shortlist", label: "Shortlist", id: "shortlist" },
  { to: "/stories", label: "Stories", id: "stories" },
  { to: "/me", label: "My Profile", id: "me" },
  { to: "/invite", label: "Invite", id: "invite" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { account, isAdmin } = useAccount();
  const { t } = usePrefs();
  const links = isAdmin ? [...LINKS, { to: "/admin", label: "Admin", id: "admin" }] : LINKS;
  const linkCls = ({ isActive }) => `text-sm tracking-wide transition-colors whitespace-nowrap ${isActive ? "text-amber-300" : "text-slate-300 hover:text-amber-200"}`;
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#08090C]/80 border-b border-amber-500/15">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
          <Heart className="w-5 h-5 text-amber-400" fill="currentColor" />
          <span className="font-display text-2xl font-semibold gold-text">Vivaah Royale</span>
        </Link>
        <nav className="hidden xl:flex items-center gap-5">
          {links.map((l) => <NavLink key={l.id} to={l.to} className={linkCls} data-testid={`nav-${l.id}-link`}>{t(l.id)}</NavLink>)}
        </nav>
        <div className="flex items-center gap-3">
          <PrefsSwitcher className="hidden md:flex" />
          <Show when="signed-in">
            <Link to="/wallet" data-testid="nav-wallet-button" className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 text-sm">
              <WalletIcon className="w-4 h-4" /> ₹<span data-testid="nav-wallet-balance">{account?.balance ?? 0}</span>
            </Link>
            <UserButton />
          </Show>
          <Show when="signed-out">
            <SignInButton><button data-testid="nav-signin-button" className="btn-gold px-5 py-2 rounded-full text-sm font-medium">{t("signin")}</button></SignInButton>
          </Show>
          <button className="xl:hidden text-slate-200" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="xl:hidden border-t border-amber-500/15 bg-[#0B0C11] px-5 py-4 grid grid-cols-2 gap-4" data-testid="nav-mobile-menu">
          {links.map((l) => <NavLink key={l.id} to={l.to} className={linkCls} onClick={() => setOpen(false)} data-testid={`nav-mobile-${l.id}-link`}>{t(l.id)}</NavLink>)}
          <NavLink to="/wallet" className={linkCls} onClick={() => setOpen(false)} data-testid="nav-mobile-wallet-link">{t("wallet")}</NavLink>
          <PrefsSwitcher className="col-span-2 md:hidden" />
        </div>
      )}
    </header>
  );
}
